import { normalizeSupportRequestStatus } from '@emporion/config/statuses'
import type { SupportRequestStatus } from '@emporion/config/statuses'
import { requestApiJson } from '../shared/api/client'
import { withCookieFirstRequest } from '../shared/api/cookieAuthRequest'

export type CustomerSupportRequestStatus = SupportRequestStatus

export type CustomerSupportRequest = {
  id: string
  userId: string
  username: string
  subject: string
  message: string
  orderId: string | null
  orderNumber: string | null
  status: CustomerSupportRequestStatus
  adminNotes: string | null
  createdAt: string
  updatedAt: string
  closedAt: string | null
}

export type CreateCustomerSupportRequestInput = {
  subject: string
  message: string
  orderId?: string
}

/**
 * Maps support API errors to customer-facing fallback messages.
 */
const toErrorMessage = (status: number, payload: unknown) => {
  if (payload && typeof payload === 'object') {
    const message = (payload as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) {
      return message.trim()
    }
  }

  if (status === 400) return 'Dati richiesta assistenza non validi'
  if (status === 401) return 'Sessione cliente non valida'
  if (status === 403) return 'Account non autorizzato per assistenza cliente'
  if (status === 404) return 'Risorsa assistenza non trovata'
  if (status === 503) return 'Servizio assistenza non raggiungibile'
  if (status === 504) return 'Timeout servizio assistenza'
  return 'Errore gestione assistenza cliente'
}

/**
 * Error raised by customer support API wrappers.
 */
export class CustomerSupportApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

/**
 * Normalizes unknown support-request payloads into strict local model.
 */
const normalizeCustomerSupportRequest = (input: Record<string, unknown>): CustomerSupportRequest => ({
  id: String(input.id ?? ''),
  userId: String(input.userId ?? ''),
  username: String(input.username ?? ''),
  subject: String(input.subject ?? ''),
  message: String(input.message ?? ''),
  orderId: typeof input.orderId === 'string' ? input.orderId : null,
  orderNumber: typeof input.orderNumber === 'string' ? input.orderNumber : null,
  status: normalizeSupportRequestStatus(input.status) ?? 'open',
  adminNotes: typeof input.adminNotes === 'string' ? input.adminNotes : null,
  createdAt: String(input.createdAt ?? ''),
  updatedAt: String(input.updatedAt ?? ''),
  closedAt: typeof input.closedAt === 'string' ? input.closedAt : null,
})

/**
 * Performs authenticated JSON requests against customer support endpoints.
 */
const requestJson = <T>(
  path: string,
  init: Omit<RequestInit, 'headers'> = {},
  token?: string | null,
) =>
  requestApiJson<T>({
    path,
    init: withCookieFirstRequest({
      init,
      bearerFallbackToken: token,
    }),
    defaultHeaders: {
      'Content-Type': 'application/json',
    },
    toErrorMessage,
    createError: (status, message) => new CustomerSupportApiError(status, message),
  })

/**
 * Creates a new support request for the authenticated customer.
 */
export const createCustomerSupportRequest = async (
  input: CreateCustomerSupportRequestInput,
  token?: string | null,
) => {
  const payload = await requestJson<{ supportRequest?: unknown }>(
    '/support/requests',
    {
      method: 'POST',
      body: JSON.stringify({
        subject: input.subject,
        message: input.message,
        orderId: input.orderId,
      }),
    },
    token,
  )
  const supportRequest = payload.supportRequest
  if (!supportRequest || typeof supportRequest !== 'object') {
    throw new CustomerSupportApiError(502, 'Risposta assistenza non valida')
  }
  return normalizeCustomerSupportRequest(supportRequest as Record<string, unknown>)
}

/**
 * Loads support requests of the authenticated customer with optional filters.
 */
export const fetchMySupportRequests = async (
  options: {
    status?: CustomerSupportRequestStatus
    limit?: number
  } = {},
  token?: string | null,
) => {
  const query = new URLSearchParams()
  if (options.status) query.set('status', options.status)
  if (typeof options.limit === 'number' && Number.isFinite(options.limit) && options.limit > 0) {
    query.set('limit', String(Math.floor(options.limit)))
  }

  const suffix = query.toString()
  const payload = await requestJson<{ supportRequests?: unknown }>(
    `/support/requests/me${suffix ? `?${suffix}` : ''}`,
    {
      method: 'GET',
    },
    token,
  )
  const supportRequests = Array.isArray(payload.supportRequests) ? payload.supportRequests : []
  return supportRequests
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
    .map((entry) => normalizeCustomerSupportRequest(entry))
}
